import React, { useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ArrowRightIcon, CalendarIcon, ClockIcon } from 'lucide-react';
import { getArticleBySlug, getRelatedArticles } from '../data/articles';
import { ArticleBody } from '../components/article/ArticleBody';
import { ArticleCTABand } from '../components/article/ArticleCTABand';
import { ArticleFAQ } from '../components/article/ArticleFAQ';
import { ArticleTOC } from '../components/article/ArticleTOC';
import { usePageMeta } from '../hooks/usePageMeta';
import { buildMatchLink } from '../utils/matchParams';
const FAQ_ID = 'faqs';
export function ArticlePage() {
  const {
    articleName
  } = useParams<{
    articleName: string;
  }>();
  const location = useLocation();
  // Each article also has an explicit route so it appears as its own URL, and
  // those routes carry no `:articleName` param — fall back to the path.
  const slug = articleName || location.pathname.split('/').filter(Boolean).pop() || '';
  const article = getArticleBySlug(slug);
  usePageMeta(article?.metaTitle || 'Article | Online College Guide', article?.metaDescription || 'Compare accredited online degree programs. Get matched free by career goals, schedule, and budget.', {
    ogType: 'article'
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!article) {
    return <main className="min-h-[60vh] flex items-center justify-center px-4 py-20" data-id="element-931" data-mp="2hu7ge28gjyg1">
        <div className="text-center max-w-md" data-id="element-932" data-mp="1u6pidoylo44m">
          <h1 className="text-2xl font-bold text-navy mb-3" data-id="element-933" data-mp="1p7wfeqgxvvip">
            Article Not Found
          </h1>
          <p className="text-gray-600 mb-6" data-id="element-934" data-mp="22z17n7hz9lj0">
            We couldn't find that article. Browse our guides or get matched with
            accredited programs instead.
          </p>
          <Link to="/request-information" className="inline-flex items-center gap-2 bg-navy hover:bg-navy-dark text-white font-bold py-3 px-7 rounded-lg text-sm transition-colors duration-200" data-id="element-935" data-mp="1ai8g1usbj6xm">
            Get Matched Free
            <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-936" data-mp="35ld6bxupdtbz" />
          </Link>
        </div>
      </main>;
  }
  const related = getRelatedArticles(article.slug);
  const midpoint = Math.ceil(article.sections.length / 2);
  const matchHref = buildMatchLink('/request-information', article.match);
  return <main className="bg-white" data-id="element-937" data-mp="3kcm4ivco8n0q">
      {/* Hero */}
      <header className="hero-bg" data-id="element-938" data-mp="2mnu5vimb9j16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-939" data-mp="2mols42u0q4es">
          <nav aria-label="Breadcrumb" className="mb-6" data-id="element-940" data-mp="2rmds7k4tgwu2">
            <ol className="flex flex-wrap items-center gap-2 text-xs text-white/70" data-id="element-941" data-mp="2wkfaw3ybyu50">
              <li data-id="element-942" data-mp="31k1oiei2mcec">
                <Link to="/" className="hover:text-white transition-colors" data-id="element-943" data-mp="31kkxv6tnnsgr">
                  Home
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-944" data-mp="bug5o28o6z63">/</li>
              <li className="text-white/90" data-id="element-945" data-mp="gtkf8tziaywy">{article.category}</li>
            </ol>
          </nav>
          <p className="text-[11px] font-semibold uppercase tracking-widest text-gold mb-4" data-id="element-946" data-mp="21bli5qprdw65">
            {article.eyebrow}
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5 max-w-4xl" data-id="element-947" data-mp="1we34xo4pkusc">
            {article.title}
          </h1>
          <p className="text-lg text-white/80 leading-relaxed max-w-3xl mb-6" data-id="element-948" data-mp="1mmypknb4f7wz">
            {article.intro}
          </p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-white/60" data-id="element-949" data-mp="1hodratffiyb6">
            <span className="inline-flex items-center gap-1.5" data-id="element-950" data-mp="1dlr6hch6ednh">
              <CalendarIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-951" data-mp="1dod9cyt4g4zd" />
              Updated {article.updated}
            </span>
            <span className="inline-flex items-center gap-1.5" data-id="element-952" data-mp="13omgt2o4g01x">
              <ClockIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-953" data-mp="13q7e301tlx3h" />
              {article.readMinutes} min read
            </span>
          </div>
        </div>
      </header>

      {/* Body */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-954" data-mp="2y016xkl1o1i7">
        <div className="flex flex-col lg:flex-row gap-12" data-id="element-955" data-mp="1vp7or1b2b8th">
          <article className="lg:w-2/3 space-y-12" data-id="element-956" data-mp="1zqt6p12gedaj">
            <ArticleBody sections={article.sections} interruptAfter={midpoint} interruption={<ArticleCTABand variant="inline" body={article.cta.body} buttonLabel={article.cta.buttonLabel} match={article.match} data-id="element-958" data-mp="1qpczt3auktb2" />} data-id="element-957" data-mp="1zpi1qxx9uzhu" />
            <ArticleFAQ id={FAQ_ID} faqs={article.faqs} data-id="element-959" data-mp="1gt07a8r1glhw" />
          </article>

          {/* Sidebar */}
          <aside className="lg:w-1/3 space-y-8" data-id="element-960" data-mp="131vb5gmvqbcb">
            <div className="lg:sticky lg:top-24 space-y-8" data-id="element-961" data-mp="17ylss8ec1ezu">
              <div className="bg-navy rounded-xl p-6" data-id="element-962" data-mp="17x0ymjvxjy2i">
                <h2 className="text-lg font-bold text-white mb-2" data-id="element-963" data-mp="t2w2vz610hpu">
                  Get Matched Free
                </h2>
                <p className="text-sm text-white/75 leading-relaxed mb-5" data-id="element-964" data-mp="y0fnrhx8bsqr">
                  Answer a few questions and we'll connect you with accredited
                  programs that fit your goals, schedule, and budget.
                </p>
                <Link to={matchHref} className="block text-center bg-cta hover:bg-cta-dark text-white font-bold py-3 px-5 rounded-lg text-sm transition-colors duration-200" data-id="element-965" data-mp="od22ahbym6y9">
                  {article.cta.buttonLabel}
                </Link>
                <p className="mt-3 text-xs text-white/55 text-center" data-id="element-966" data-mp="tcepx4anni1w">
                  Free for students. No obligation.
                </p>
              </div>

              <ArticleTOC sections={article.sections} faqId={FAQ_ID} data-id="element-967" data-mp="1731l4mh7ipt8" />

              <div className="border-t border-gray-200 pt-6" data-id="element-968" data-mp="125rpuq74zjxh">
                <h2 className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-4" data-id="element-969" data-mp="1h20njnbdpp7s">
                  Keep Reading
                </h2>
                <ul className="space-y-4" data-id="element-970" data-mp="1c4h6ygr7l0co">
                  {article.internalLinks.map(link => <li key={link.anchor} data-id="element-971" data-mp="1j7p578yocwj8">
                      <Link to={buildMatchLink(link.to, link.match ?? article.match)} className="group flex items-start gap-2 text-sm text-gray-700 hover:text-navy transition-colors duration-150" data-id="element-972" data-mp="1o4xoxdzh18ml">
                        <ArrowRightIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400 group-hover:text-navy transition-colors duration-150" aria-hidden="true" data-id="element-973" data-mp="22tk2ba5jfqb6" />
                        <span className="capitalize" data-id="element-974" data-mp="2gtus09v42yur">{link.anchor}</span>
                      </Link>
                    </li>)}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Related articles */}
      {related.length > 0 && <section aria-labelledby="related-heading" className="bg-light border-t border-gray-200 py-14" data-id="element-975" data-mp="ew2j8mxd1crw">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-976" data-mp="a4kq2l1kwghh">
            <h2 id="related-heading" className="text-2xl font-bold text-navy mb-6" data-id="element-977" data-mp="569fu97dg3yq">
              Related Guides
            </h2>
            <ul className="grid sm:grid-cols-3 gap-5" data-id="element-978" data-mp="5lhf2gtckuh">
              {related.map(item => <li key={item.slug} className="flex" data-id="element-979" data-mp="xt3xyzgpur91">
                  <Link to={`/article/${item.slug}`} className="group bg-white border border-gray-200 rounded-xl p-6 flex flex-col w-full hover:border-navy transition-colors duration-200" data-id="element-980" data-mp="sx5817fqp16i">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2" data-id="element-981" data-mp="j0a7kizwoqpl">
                      {item.category}
                    </span>
                    <span className="font-bold text-gray-900 leading-snug mb-3" data-id="element-982" data-mp="33mlmrvjbyhej">
                      {item.title}
                    </span>
                    <span className="mt-auto inline-flex items-center text-sm font-semibold text-navy" data-id="element-983" data-mp="25pv19wa3j1t">
                      Read guide
                      <ArrowRightIcon className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform duration-200" aria-hidden="true" data-id="element-984" data-mp="3kcxg4axf4kiv" />
                    </span>
                  </Link>
                </li>)}
            </ul>
          </div>
        </section>}

      <ArticleCTABand body={article.cta.body} buttonLabel={article.cta.buttonLabel} match={article.match} data-id="element-985" data-mp="75c5xcomwn7g" />
    </main>;
}